import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { mediaUrl } from '../api/client';
import {
  useAddBlackout,
  useCompliance,
  useDeleteBlackout,
  useReports,
  useResolveReport,
} from '../api/queries';
import type { ContentReport, ReportReason, ReportStatus } from '../api/types';
import ui from '../components/ui.module.css';
import { dateTimeLabel, errorMessage } from '../copy';
import styles from './Compliance.module.css';

/**
 * Conformidade (legal review 2026-09-28): the two things the law asks an
 * administrator to watch rather than configure once.
 *
 *   - Período eleitoral: the calendar the API applies to political polls, and
 *     the extra windows it cannot know (eleição suplementar, ordem judicial).
 *   - Denúncias: takedown requests filed from the app — a picture used without
 *     permission, a trademark, an offensive candidate. Resolving one records the
 *     decision; the fix itself (swap the picture, rename, delete) is done in the
 *     poll editor.
 */

const REASONS: Record<ReportReason, string> = {
  copyright: 'Direito autoral',
  image_rights: 'Direito de imagem',
  trademark: 'Marca',
  offensive: 'Ofensivo / falso',
  electoral: 'Eleitoral',
  other: 'Outro',
};

const STATUS: Record<ReportStatus, string> = {
  open: 'ABERTA',
  resolved: 'RESOLVIDA',
  rejected: 'RECUSADA',
};

/** `<input type="datetime-local">` holds Brasília wall time; the API wants an instant. */
const brasiliaInstant = (local: string) => new Date(`${local}:00-03:00`).toISOString();

export function Compliance(): ReactNode {
  return (
    <div className={styles.stack}>
      <Electoral />
      <Reports />
    </div>
  );
}

function Electoral(): ReactNode {
  const { data, isLoading, error } = useCompliance();
  const add = useAddBlackout();
  const remove = useDeleteBlackout();
  const [form, setForm] = useState({ startsAt: '', endsAt: '', reason: '' });
  const [failure, setFailure] = useState<string | null>(null);

  const canAdd = form.startsAt && form.endsAt && form.reason.trim().length >= 3;

  return (
    <section className={ui.panel}>
      <h1 className={ui.h1}>Período eleitoral</h1>
      <p className={styles.lead}>
        Enquetes marcadas como <strong>políticas</strong> saem do ar sozinhas em todo ano par, de 16 de agosto
        até o fim do último domingo de outubro (Res. TSE 23.600/2019, art. 23), e em cada período extra abaixo.
      </p>

      {error ? <p className={ui.error}>{errorMessage(error)}</p> : null}
      {isLoading ? <p className={ui.muted}>Carregando…</p> : null}
      {data ? (
        <p className={data.electoral.blackout ? styles.alert : styles.ok}>
          {data.electoral.blackout
            ? `Vedação em vigor até ${dateTimeLabel(data.electoral.until)} — enquetes políticas estão fora do ar.`
            : 'Sem vedação agora — enquetes políticas estão no ar.'}
        </p>
      ) : null}

      <h2 className={`${ui.h2} ${styles.section}`}>Períodos extras</h2>
      {failure ? <p className={ui.error}>{failure}</p> : null}
      <div className={styles.form}>
        <label className={ui.field}>
          <span className={ui.label}>Início (horário de Brasília)</span>
          <input
            type="datetime-local"
            className={ui.input}
            value={form.startsAt}
            onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
          />
        </label>
        <label className={ui.field}>
          <span className={ui.label}>Fim</span>
          <input
            type="datetime-local"
            className={ui.input}
            value={form.endsAt}
            onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
          />
        </label>
        <label className={`${ui.field} ${styles.grow}`}>
          <span className={ui.label}>Motivo</span>
          <input
            className={ui.input}
            placeholder="Eleição suplementar — município/UF"
            maxLength={200}
            value={form.reason}
            onChange={(event) => setForm({ ...form, reason: event.target.value })}
          />
        </label>
        <button
          type="button"
          className={ui.primary}
          disabled={!canAdd || add.isPending}
          onClick={() => {
            setFailure(null);
            add.mutate(
              {
                startsAt: brasiliaInstant(form.startsAt),
                endsAt: brasiliaInstant(form.endsAt),
                reason: form.reason.trim(),
              },
              {
                onSuccess: () => setForm({ startsAt: '', endsAt: '', reason: '' }),
                onError: (cause) => setFailure(errorMessage(cause)),
              },
            );
          }}
        >
          Adicionar
        </button>
      </div>

      {data && data.blackouts.length === 0 ? <p className={ui.muted}>Nenhum período extra.</p> : null}
      {data && data.blackouts.length > 0 ? (
        <ul className={styles.list}>
          {data.blackouts.map((row) => (
            <li key={row.id} className={styles.item}>
              <span>
                <strong>{row.reason}</strong>
                <span className={ui.muted}>
                  {' '}
                  {dateTimeLabel(row.starts_at)} → {dateTimeLabel(row.ends_at)}
                </span>
              </span>
              <button
                type="button"
                className={`${ui.ghost} ${ui.small}`}
                disabled={remove.isPending}
                onClick={() => {
                  setFailure(null);
                  remove.mutate(row.id, { onError: (cause) => setFailure(errorMessage(cause)) });
                }}
              >
                Remover
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function Reports(): ReactNode {
  const { data: reports, isLoading, error } = useReports();
  const [filter, setFilter] = useState<ReportStatus | 'all'>('open');
  const shown = (reports ?? []).filter((report) => filter === 'all' || report.status === filter);
  const open = (reports ?? []).filter((report) => report.status === 'open').length;

  return (
    <section className={ui.panel}>
      <div className={ui.spread}>
        <h1 className={ui.h1}>Denúncias {open ? <span className={`${ui.badge} ${ui.badgePaused}`}>{open}</span> : null}</h1>
        <select
          aria-label="Filtrar denúncias"
          className={ui.select}
          value={filter}
          onChange={(event) => setFilter(event.target.value as ReportStatus | 'all')}
        >
          <option value="open">Abertas</option>
          <option value="resolved">Resolvidas</option>
          <option value="rejected">Recusadas</option>
          <option value="all">Todas</option>
        </select>
      </div>
      <p className={styles.lead}>
        Pedidos de remoção feitos pelo app: imagem sem autorização, marca, conteúdo ofensivo. Responda em até
        48 horas — é o prazo informado nos Termos de Uso.
      </p>

      {error ? <p className={ui.error}>{errorMessage(error)}</p> : null}
      {isLoading ? <p className={ui.muted}>Carregando…</p> : null}
      {reports && shown.length === 0 ? <p className={ui.muted}>Nada aqui.</p> : null}
      <ul className={styles.list}>
        {shown.map((report) => (
          <ReportItem key={report.id} report={report} />
        ))}
      </ul>
    </section>
  );
}

function ReportItem({ report }: { report: ContentReport }): ReactNode {
  const resolve = useResolveReport();
  const [resolution, setResolution] = useState(report.resolution ?? '');
  const [failure, setFailure] = useState<string | null>(null);

  const decide = (status: ReportStatus) => {
    setFailure(null);
    resolve.mutate(
      { id: report.id, status, resolution: resolution.trim() || null },
      { onError: (cause) => setFailure(errorMessage(cause)) },
    );
  };

  return (
    <li className={styles.report}>
      <div className={styles.reportHead}>
        {report.candidate_image ? <img className={ui.thumb} src={mediaUrl(report.candidate_image)} alt="" /> : null}
        <div className={styles.grow}>
          <div className={ui.row}>
            <span className={`${ui.badge} ${report.status === 'open' ? ui.badgePaused : ui.badgeClosed}`}>
              {STATUS[report.status]}
            </span>
            <strong>{REASONS[report.reason]}</strong>
            <span className={ui.muted}>{dateTimeLabel(report.created_at)}</span>
          </div>
          <p className={styles.target}>
            {report.poll_title ?? 'Enquete apagada'}
            {report.candidate_name ? ` › ${report.candidate_name}` : ''}
          </p>
        </div>
        {report.poll_id ? (
          <Link className={`${ui.ghost} ${ui.small} ${styles.link}`} to={`/enquetes/${report.poll_id}`}>
            Abrir enquete
          </Link>
        ) : null}
      </div>
      <p className={styles.details}>{report.details}</p>
      {report.contact ? <p className={ui.muted}>Contato: {report.contact}</p> : null}
      {failure ? <p className={ui.error}>{failure}</p> : null}
      <div className={styles.form}>
        <input
          className={`${ui.input} ${styles.grow}`}
          aria-label="Decisão"
          placeholder="Decisão tomada (ex.: foto substituída)"
          maxLength={2000}
          value={resolution}
          onChange={(event) => setResolution(event.target.value)}
        />
        {report.status === 'open' ? (
          <>
            <button type="button" className={ui.primary} disabled={resolve.isPending} onClick={() => decide('resolved')}>
              Resolver
            </button>
            <button type="button" className={ui.ghost} disabled={resolve.isPending} onClick={() => decide('rejected')}>
              Recusar
            </button>
          </>
        ) : (
          <button type="button" className={ui.ghost} disabled={resolve.isPending} onClick={() => decide('open')}>
            Reabrir
          </button>
        )}
      </div>
    </li>
  );
}
